import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["type", "weeklyOptions", "monthlyOptions", "repeatOn", "shortMonthNote"]
  static values = { recurrenceType: String, shortMonthCallout: String };

  connect() {
    this.toggleFrequency({ target: { value: this.typeTarget.value } });
    this.updateRepeatOptions();
  }

  toggleFrequency(event) {
    const frequency = event.target.value;
    if (frequency === "weekly") {
      this.weeklyOptionsTarget.classList.remove("d-none");
      this.monthlyOptionsTarget.classList.add("d-none");
    } else if (frequency === "monthly") {
      this.weeklyOptionsTarget.classList.add("d-none");
      this.monthlyOptionsTarget.classList.remove("d-none");
      this.updateShortMonthNote();
    } else {
      this.weeklyOptionsTarget.classList.add("d-none");
      this.monthlyOptionsTarget.classList.add("d-none");
      if (this.hasShortMonthNoteTarget) this.shortMonthNoteTarget.hidden = true;
    }
  }

  updateRepeatOptions() {
    // The start-date field is `start_date_local` (flatpickr keeps the Y-m-d value
    // on this original input even though altInput shows a second one). The old
    // selector looked for `event[start_date]`, which never matched, so the monthly
    // "Repeat on" options never populated.
    const startDateInput = this.element.querySelector('input[name="event[start_date_local]"]');
    if (!startDateInput || !startDateInput.value) return;

    // Parse "YYYY-MM-DD" as a LOCAL date. `new Date("2026-06-23")` is UTC
    // midnight, which getDate() reads back a day early in negative-offset
    // timezones (US) — so build the date from its parts instead.
    const [y, mo, d] = startDateInput.value.split("-").map((n) => parseInt(n, 10));
    if (!y || !mo || !d) return;
    const startDate = new Date(y, mo - 1, d);
    if (isNaN(startDate)) return;

    const dayOfMonth = startDate.getDate();
    const dayOfWeek = startDate.toLocaleString('default', { weekday: 'long' }).toLowerCase();
    const weekOfMonth = Math.ceil(dayOfMonth / 7);
    const lastDayOfMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).getDate();
    const isLastWeek = (lastDayOfMonth - dayOfMonth) < 7;

    const dayOfMonthRule = `day_of_month(${dayOfMonth})`;
    const dayOfWeekRule = isLastWeek
        ? `day_of_week(:${dayOfWeek} => [-1])`
        : `day_of_week(:${dayOfWeek} => [${weekOfMonth}])`;

    const currentValue = this.repeatOnTarget.value;

    this.clearSelectOptions(this.repeatOnTarget);
    this.addOption(this.repeatOnTarget, dayOfMonthRule, `Every ${dayOfMonth}${this.getOrdinalSuffix(dayOfMonth)} of the month`, currentValue);
    this.addOption(this.repeatOnTarget, dayOfWeekRule, isLastWeek ? `Every last ${dayOfWeek} of the month` : `Every ${this.getOrdinalText(weekOfMonth)} ${dayOfWeek} of the month`, currentValue);
    this.updateShortMonthNote();
  }

  updateShortMonthNote() {
    if (!this.hasShortMonthNoteTarget) return;
    if (this.monthlyOptionsTarget.classList.contains("d-none")) {
      this.shortMonthNoteTarget.hidden = true;
      return;
    }

    const rule = this.hasRepeatOnTarget ? this.repeatOnTarget.value : "";
    const dom = rule.match(/day_of_month\((\d{1,2})\)/);
    const day = dom ? parseInt(dom[1], 10) : null;
    const show = day != null && day >= 29;

    this.shortMonthNoteTarget.hidden = !show;
    if (!show) return;

    const ordinalDay = `${day}${this.getOrdinalSuffix(day)}`;
    const template = this.hasShortMonthCalloutValue
      ? this.shortMonthCalloutValue
      : "Months without a %{day} will use the last day of the month.";
    this.shortMonthNoteTarget.textContent = template.replace("%{day}", ordinalDay);
  }

  clearSelectOptions(selectElement) {
    while (selectElement.options.length > 0) {
      selectElement.remove(0);
    }
  }

  addOption(selectElement, value, text, defaultValue) {
    const option = document.createElement("option");
    option.value = value;
    option.text = text;
    if (value === defaultValue) {
      option.selected = true;
    }
    selectElement.appendChild(option);
  }

  getOrdinalSuffix(day) {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  getOrdinalText(weekNumber) {
    switch (weekNumber) {
      case 1: return 'First';
      case 2: return 'Second';
      case 3: return 'Third';
      case 4: return 'Fourth';
      case 5: return 'Fifth';
      default: return '';
    }
  }
}
